/**
 * BSC代币价格监控组件
 * 通过WebSocket连接获取并显示代币的实时价格信息
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { TokenInfo } from "../types/dex";
import { useBscWebSocket } from "../contexts/BscWebSocketContext";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Skeleton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import SyncIcon from "@mui/icons-material/Sync";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import RefreshIcon from "@mui/icons-material/Refresh";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useDexConfigRedux } from "../hooks/useDexConfigRedux";

/**
 * 价格数据接口
 */
interface PriceData {
  price: number;
  change24h: number;
  volume24h: number;
  high24h: number;
  low24h: number;
  lastUpdate: Date;
  isLoading: boolean;
}

/**
 * BSC价格监控组件属性
 */
interface BscPriceMonitorProps {
}

/**
 * 格式化数字为易读形式
 * @param value 要格式化的数字
 * @param decimals 保留的小数位数，默认为2
 * @returns 格式化后的字符串
 */
const formatNumber = (value: number, decimals: number = 2): string => {
  if (value === undefined || value === null || isNaN(value)) {
    return "0";
  }
  if (value !== 0 && Math.abs(value) < 0.000001) {
    return value.toExponential(decimals);
  }
  if (Math.abs(value) < 1) {
    const significantDigits = Math.max(6, decimals);
    return value.toFixed(significantDigits).replace(/\.?0+$/, "");
  }
  if (Math.abs(value) >= 1000000000) {
    return (value / 1000000000).toFixed(decimals) + "B";
  } else if (Math.abs(value) >= 1000000) {
    return (value / 1000000).toFixed(decimals) + "M";
  } else if (Math.abs(value) >= 1000) {
    return (value / 1000).toFixed(decimals) + "K";
  }
  return value.toFixed(decimals).replace(/\.?0+$/, "");
};

/**
 * 使用防抖函数，避免频繁更新
 * @param fn 要执行的函数
 * @param delay 延迟时间（毫秒）
 * @returns 防抖处理后的函数
 */
function useDebounce<T extends (...args: any[]) => any>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  return useCallback(
    (...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      timeoutRef.current = setTimeout(() => {
        fn(...args);
        timeoutRef.current = null;
      }, delay);
    },
    [fn, delay]
  );
}

/**
 * BSC代币价格监控组件
 * 通过WebSocket连接获取并显示代币的实时价格信息
 * @param props 组件属性
 * @returns React组件
 */
const BscPriceMonitor: React.FC<BscPriceMonitorProps> = ({
 
}) => {
  const theme = useTheme();
  
  const {
    swapConfig
  } = useDexConfigRedux();


  const token2 = swapConfig?.token2;
  const token1 = swapConfig?.token1;

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { status, error, subscribe, unsubscribe, onPriceUpdate } =
    useBscWebSocket();
  const [priceData, setPriceData] = useState<PriceData>({
    price: 0,
    change24h: 0,
    volume24h: 0,
    high24h: 0,
    low24h: 0,
    lastUpdate: new Date(),
    isLoading: true,
  });
  const [tokenName, setTokenName] = useState<string>("");
  const [refreshing, setRefreshing] = useState<boolean>(false);
  
  // 使用ref保存上一次的价格数据，用于比较变化
  const lastPriceDataRef = useRef<PriceData | null>(null);
  
  // 使用防抖函数更新价格数据，避免频繁更新导致的闪烁
  const debouncedSetPriceData = useDebounce((newData: PriceData) => {
    // 只有当价格数据有实质性变化时才更新
    if (!lastPriceDataRef.current || 
        Math.abs(newData.price - lastPriceDataRef.current.price) > 0.000001 || 
        Math.abs(newData.change24h - lastPriceDataRef.current.change24h) > 0.01) {
      setPriceData(newData);
      lastPriceDataRef.current = newData;
    }
  }, 300); // 300毫秒的防抖延迟

  // 处理价格更新
  useEffect(() => {
    const priceUpdateCallback = (marketData: any) => {
      // console.log("BSC价格更新收到:", marketData);

      try {
        // 检查marketData是否为数组，并且有数据
        if (Array.isArray(marketData) && marketData.length > 0) {
          const tokenData = marketData[0];

          // 解析新的BSC WebSocket数据格式
          // 根据实际返回的数据结构进行解析
          const price = parseFloat(tokenData.price || tokenData.last || "0");
          const change = parseFloat(
            tokenData.change || tokenData.changePercent || "0"
          );
          const volume = parseFloat(
            tokenData.volume || tokenData.vol24h || "0"
          );
          const highPrice = parseFloat(
            tokenData.maxPrice || tokenData.high24h || "0"
          );
          const lowPrice = parseFloat(
            tokenData.minPrice || tokenData.low24h || "0"
          );

          // 使用防抖函数更新价格数据
          debouncedSetPriceData({
            price: price,
            change24h: change,
            volume24h: volume,
            high24h: highPrice,
            low24h: lowPrice,
            lastUpdate: new Date(),
            isLoading: false,
          });
        } else if (typeof marketData === "object" && marketData !== null) {
          // 处理单个对象数据
          const price = parseFloat(marketData.price || marketData.last || "0");
          const change = parseFloat(
            marketData.change || marketData.changePercent || "0"
          );
          const volume = parseFloat(
            marketData.volume || marketData.vol24h || "0"
          );
          const highPrice = parseFloat(
            marketData.maxPrice || marketData.high24h || "0"
          );
          const lowPrice = parseFloat(
            marketData.minPrice || marketData.low24h || "0"
          );

          // 使用防抖函数更新价格数据
          debouncedSetPriceData({
            price: price,
            change24h: change,
            volume24h: volume,
            high24h: highPrice,
            low24h: lowPrice,
            lastUpdate: new Date(),
            isLoading: false,
          });
        } else {
          console.warn("收到的市场数据格式不正确:", marketData);
        }
      } catch (err) {
        console.error("解析价格数据出错:", err);
      }

      setRefreshing(false);
    };
    
    // 注册价格更新回调
    const cleanupCallback = onPriceUpdate(priceUpdateCallback);
    
    // 清理函数
    return () => {
      // 调用清理函数取消订阅
      if (cleanupCallback) {
        cleanupCallback();
      }
    };
  }, [onPriceUpdate, debouncedSetPriceData]);

  // 订阅价格数据
  useEffect(() => {
    console.log("订阅代币价格状态变化:", status, token2?.address); 
    if (status === "connected" && token2?.address) {
      setPriceData((prev) => ({ ...prev, isLoading: true }));
      // 订阅代币对价格，例如：TOKEN1-TOKEN2
      const tokenAddress = token2.address;
      
      // 先取消之前的订阅
      unsubscribe(tokenAddress);
      // 然后重新订阅
      console.log("开始订阅代币:", tokenAddress);
      subscribe(tokenAddress);
      
      // 组件卸载时取消订阅
      return () => {
        console.log("取消订阅代币:", tokenAddress);
        unsubscribe(tokenAddress);
      };
    }
  }, [status, subscribe, unsubscribe, token2?.address]);

  // 添加一个额外的副作用，用于监控状态变化
  useEffect(() => {
    console.log("WebSocket连接状态:", status);
  }, [status]);

  // 设置代币名称
  useEffect(() => {
    if (token2) {
      setTokenName(token2.symbol);
    } else {
      setTokenName("未选择代币");
    }
  }, [token2]);

  // 手动刷新价格数据
  const handleRefresh = useCallback(() => {
    if (status === "connected" && token2) {
      setRefreshing(true);
      const tokenAddress = token2.address;
      
      // 先取消订阅，然后重新订阅以获取最新数据
      unsubscribe(tokenAddress);
      
      // 确保取消订阅完成后再重新订阅
      setTimeout(() => {
        subscribe(tokenAddress);
      }, 500);
    }
  }, [status, subscribe, unsubscribe, token2, refreshing]);

  // 获取连接状态图标和颜色
  const getConnectionStatus = () => {
    if (status === "connected") {
      return {
        icon: <CheckCircleIcon fontSize="small" />,
        color: "success",
        text: "BSC-WS 已连接",
      };
    } else if (status === "connecting") {
      return {
        icon: <SyncIcon fontSize="small" className="rotating" />,
        color: "warning",
        text: "BSC-WS 连接中",
      };
    } else {
      return {
        icon: <ErrorIcon fontSize="small" />,
        color: "error",
        text: `BSC-WS 已断开 ${error ? `(${error})` : ""}`,
      };
    }
  };

  // 获取价格变化图标
  const getPriceChangeIcon = () => {
    if (priceData.change24h > 0) {
      return <TrendingUpIcon fontSize="small" />;
    } else if (priceData.change24h < 0) {
      return <TrendingDownIcon fontSize="small" />;
    } else {
      return <TrendingFlatIcon fontSize="small" />;
    }
  };

  const connectionStatus = getConnectionStatus();

  // 价格变化颜色
  const getPriceChangeColor = () => {
    if (priceData.change24h > 0) {
      return theme.palette.success.main;
    } else if (priceData.change24h < 0) {
      return theme.palette.error.main;
    }
    return theme.palette.text.primary;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 500 }}>
          {tokenName} 价格监控
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Tooltip title="刷新价格数据">
            <IconButton
              size="small"
              onClick={handleRefresh}
              disabled={status !== "connected" || refreshing}
              sx={{ mr: 1 }}
            >
              {refreshing ? (
                <CircularProgress size={20} />
              ) : (
                <RefreshIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <Chip
            icon={connectionStatus.icon}
            label={connectionStatus.text}
            color={connectionStatus.color as any}
            size="small"
            variant="outlined"
            sx={{
              "& .rotating": {
                animation: "spin 2s linear infinite",
                "@keyframes spin": {
                  "0%": { transform: "rotate(0deg)" },
                  "100%": { transform: "rotate(360deg)" },
                },
              },
            }}
          />
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} variant="outlined">
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              bgcolor: "action.hover",
              borderRadius: 2,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography variant="caption" color="text.secondary" gutterBottom>
              当前价格
            </Typography>
            {priceData.isLoading ? (
              <Skeleton variant="text" width="80%" height={40} />
            ) : (
              <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
                ${formatNumber(priceData.price)}
              </Typography>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              bgcolor: "action.hover",
              borderRadius: 2,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography variant="caption" color="text.secondary" gutterBottom>
              24h涨跌
            </Typography>
            {priceData.isLoading ? (
              <Skeleton variant="text" width="60%" height={40} />
            ) : (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {getPriceChangeIcon()}
                <Typography
                  variant="h5"
                  component="div"
                  sx={{
                    fontWeight: 600,
                    color: getPriceChangeColor(),
                    ml: 0.5,
                  }}
                >
                  {priceData.change24h > 0 ? "+" : ""}
                  {formatNumber(priceData.change24h)}%
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* 24小时高低价格 */}
      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={6}>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              bgcolor: "action.hover",
              borderRadius: 2,
              height: "100%",
            }}
          >
            <Typography variant="caption" color="text.secondary" gutterBottom>
              24h最高
            </Typography>
            {priceData.isLoading ? (
              <Skeleton variant="text" width="60%" height={30} />
            ) : (
              <Typography
                variant="body1"
                component="div"
                sx={{ fontWeight: 500, color: theme.palette.success.main }}
              >
                ${formatNumber(priceData.high24h)}
              </Typography>
            )}
          </Paper>
        </Grid>
        <Grid item xs={6}>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              bgcolor: "action.hover",
              borderRadius: 2,
              height: "100%",
            }}
          >
            <Typography variant="caption" color="text.secondary" gutterBottom>
              24h最低
            </Typography>
            {priceData.isLoading ? (
              <Skeleton variant="text" width="60%" height={30} />
            ) : (
              <Typography
                variant="body1"
                component="div"
                sx={{ fontWeight: 500, color: theme.palette.error.main }}
              >
                ${formatNumber(priceData.low24h)}
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* 24小时交易量 */}
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mt: 2,
          bgcolor: "action.hover",
          borderRadius: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography variant="caption" color="text.secondary">
            24h交易量
          </Typography>
          <Tooltip title="过去24小时内的交易总量">
            <InfoOutlinedIcon
              fontSize="small"
              sx={{ ml: 0.5, color: "text.secondary", fontSize: 16 }}
            />
          </Tooltip>
        </Box>
        {priceData.isLoading ? (
          <Skeleton variant="text" width="40%" height={24} />
        ) : (
          <Typography variant="body2" component="div" sx={{ fontWeight: 500 }}>
            ${formatNumber(priceData.volume24h)}
          </Typography>
        )}
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          suppressHydrationWarning
        >
          最后更新: {priceData.lastUpdate.toLocaleTimeString()}
        </Typography>
      </Box>
    </Paper>
  );
};

export default BscPriceMonitor;
